import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/core';
import type { Transaction } from '@tiptap/pm/state';
import { LayoutGrid, X, ChevronLeft, ChevronRight, Trash2, Plus } from 'lucide-react';
import {
  IMAGE_GROUP_COLUMNS,
  IMAGE_GROUP_MAX,
  type ImageGroupSnapshot,
  followImageGroup,
  readImageGroup,
  setImageGroupAttrs,
  setGroupImageAlt,
  moveImageInGroup,
  removeImageFromGroup,
} from '../lib/blogImageGroupExtension';

interface ImageGroupModalProps {
  editor: Editor;
  groupPos: number;
  onClose: () => void;
  onAddFiles: (groupPos: number) => void;
}

// Live weergave van de groep in het document: elke handeling is direct een transactie in de editor
// (en dus met undo terug te draaien). Daarom alleen een knop "Klaar", geen Opslaan/Annuleren.
export function ImageGroupModal({ editor, groupPos, onClose, onAddFiles }: ImageGroupModalProps) {
  const [group, setGroup] = useState<ImageGroupSnapshot | null>(() => readImageGroup(editor, groupPos));
  const posRef = useRef(groupPos);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const startteLeegRef = useRef(group === null);

  // Sluiten mag maar één keer. Sluiten geeft de editor de focus terug, en dat is zelf ook weer een
  // transactie; zonder deze rem zou de transactie-luisteraar hieronder daarop opnieuw sluiten, opnieuw
  // focussen, enzovoort tot de aanroepstapel vol is (React 18 stelt het verdwijnen van dit venster
  // namelijk uit tot na de luisteraar).
  const closedRef = useRef(false);
  const close = useCallback(() => {
    if (closedRef.current) return;
    closedRef.current = true;
    onCloseRef.current();
  }, []);

  // Volg de groep door transacties heen: posities schuiven als er eerder in het document iets
  // verandert (bijvoorbeeld een mislukte upload die verdwijnt). Geen groep meer op die plek: sluiten.
  // useLayoutEffect, zodat het aanmelden vóór de eerste browser-paint gebeurt; de blur-transactie van de
  // autoFocus kan er nog net voor zitten, maar die wijzigt het document niet en telt dus niet mee.
  useLayoutEffect(() => {
    const sync = ({ transaction }: { transaction: Transaction }) => {
      if (closedRef.current) return;
      // Alleen documentwijzigingen: een groep kan niet verdwijnen of verschuiven zonder docChanged,
      // en zo herrendert het venster niet bij elke selectie- of focus-transactie.
      if (!transaction.docChanged) return;
      const next = followImageGroup(editor, transaction, posRef.current);
      if (!next) {
        // Eerst afmelden, dan pas sluiten: de focus-transactie van het sluiten mag hier niet meer binnenkomen.
        editor.off('transaction', sync);
        close();
        return;
      }
      posRef.current = next.pos;
      setGroup(next);
    };
    editor.on('transaction', sync);
    return () => {
      editor.off('transaction', sync);
    };
  }, [editor, close]);

  // Stond er bij het openen al geen groep op deze plek, dan meteen sluiten (in een effect, niet tijdens de render).
  useEffect(() => {
    if (startteLeegRef.current) close();
  }, [close]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);

  if (!group) return null;

  const { pos } = group;
  const vierkant = group.columns >= 4;
  // In het venster nooit meer dan 3 tegels per rij, anders worden de alt-velden onleesbaar smal.
  const vensterKolommen = Math.min(group.columns, 3);
  const vol = group.images.length >= IMAGE_GROUP_MAX;

  const verwijder = (index: number) => {
    // Sluiten hoeft hier niet: valt de groep terug op één of nul beelden, dan ziet de transactie-luisteraar dat.
    removeImageFromGroup(editor, pos, index);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="beeldgroep-venster-titel"
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 id="beeldgroep-venster-titel" className="text-lg font-semibold text-[#231F20] flex items-center gap-2">
            <LayoutGrid className="w-5 h-5 text-[#FF6B35]" />
            Beeldgroep bewerken
          </h2>
          <button
            type="button"
            onClick={close}
            className="p-1.5 rounded hover:bg-gray-100 transition-colors text-gray-500"
            aria-label="Sluiten"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 overflow-y-auto">
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Kolommen</p>
            <div className="flex gap-2">
              {IMAGE_GROUP_COLUMNS.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setImageGroupAttrs(editor, pos, { columns: n })}
                  aria-pressed={group.columns === n}
                  className={`w-12 h-10 rounded-lg text-sm font-medium transition-colors ${
                    group.columns === n ? 'bg-[#FF6B35] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Liggende tegels bij 2 en 3, vierkante bij 4 en 6. Op een telefoon worden 4 kolommen er 2 en 6 kolommen er 3.
            </p>
          </div>

          <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${vensterKolommen}, minmax(0, 1fr))` }}>
            {/* key op de index: bij verplaatsen blijft de focus daardoor op dezelfde plek in het raster staan, niet op dezelfde foto. */}
            {group.images.map((tile, index) => (
              <div key={index} className="space-y-2">
                <img
                  src={tile.src}
                  alt=""
                  className={`w-full object-cover rounded-lg bg-gray-100 ${vierkant ? 'aspect-square' : 'aspect-[4/3]'} ${
                    tile.uploading ? 'opacity-40' : ''
                  }`}
                />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => moveImageInGroup(editor, pos, index, index - 1)}
                      disabled={index === 0}
                      className="p-1.5 rounded hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label="Naar voren"
                      title="Naar voren"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveImageInGroup(editor, pos, index, index + 1)}
                      disabled={index === group.images.length - 1}
                      className="p-1.5 rounded hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      aria-label="Naar achteren"
                      title="Naar achteren"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => verwijder(index)}
                    className="p-1.5 rounded text-red-600 hover:bg-red-50 transition-colors"
                    aria-label="Foto verwijderen"
                    title="Foto verwijderen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <input
                  type="text"
                  value={tile.alt ?? ''}
                  onChange={(e) => setGroupImageAlt(editor, pos, index, e.target.value)}
                  className="input-field text-sm"
                  placeholder="Alt-tekst"
                  aria-label={`Alt-tekst foto ${index + 1}`}
                  // De focus hoort in het venster te beginnen, niet in de tekst eronder.
                  autoFocus={index === 0}
                />
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => onAddFiles(pos)}
              disabled={vol}
              className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              Foto's toevoegen
            </button>
            <p className="text-xs text-gray-500">
              {vol ? `Maximaal ${IMAGE_GROUP_MAX} foto's, de groep is vol.` : `Maximaal ${IMAGE_GROUP_MAX} foto's per groep.`}
            </p>
          </div>

          <div>
            <label htmlFor="beeldgroep-bijschrift" className="block text-sm font-medium text-gray-700 mb-1">
              Bijschrift
            </label>
            <input
              id="beeldgroep-bijschrift"
              type="text"
              value={group.caption ?? ''}
              onChange={(e) => setImageGroupAttrs(editor, pos, { caption: e.target.value || null })}
              className="input-field"
              placeholder="Tekst onder de groep (optioneel)"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between gap-3">
          <p className="text-xs text-gray-500">Elke stap staat meteen in je tekst en is met ongedaan maken terug te draaien.</p>
          <button
            type="button"
            onClick={close}
            className="px-4 py-2 text-sm font-medium bg-[#FF6B35] text-white rounded-lg hover:bg-[#E55A2B] transition-colors"
          >
            Klaar
          </button>
        </div>
      </div>
    </div>
  );
}
